import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, 
  Search, 
  Mic, 
  Plus, 
  Filter, 
  Trash2, 
  User, 
  CheckCircle, 
  Calendar, 
  Clock, 
  Tag, 
  Image as ImageIcon,
  X,
  Loader2,
  AlertCircle,
  ChevronRight,
  MapPin,
  Phone,
  Upload,
  ShieldCheck,
  Star,
  QrCode,
  Download,
  Leaf,
  Camera,
  StarOff,
  ChevronUp,
  ChevronDown,
  Move,
  FileText
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { QRCodeCanvas } from 'qrcode.react';
import { 
  collection, 
  query, 
  onSnapshot, 
  addDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp, 
  orderBy,
  where,
  getDoc,
  setDoc,
  getDocs,
  writeBatch,
  updateDoc
} from 'firebase/firestore';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { CultivationMap } from './CultivationMap';
import { translations, Language } from '../translations';
import { Toast, ConfirmDialog } from './UI';
import { UserProfile, Rating } from '../types';

interface Listing {
  id: string;
  authorId: string;
  authorName: string;
  category: string;
  productName: string;
  price: number;
  unit: string;
  harvestDate: string;
  shelfLife: string;
  description: string;
  photoUrl: string;
  createdAt: any;
  farmingMethod?: string;
  isVerified?: boolean;
}

export default function MarketplaceScreen({ language, t, config, isAdmin, userRole, initialShowAdd, onResetInitialAdd, showMyProductsOnly, initialFarmerId, onClearInitialFarmerId }: { 
  language: string, 
  t: any,
  config: { 
    availableCertifications: string[],
    farmingMethods: string[],
    productCategories: string[],
    visibleFields: {
      location: boolean;
      contact: boolean;
      certificate: boolean;
      farmingMethods: boolean;
    }
  },
  isAdmin: boolean,
  userRole?: string,
  initialShowAdd?: boolean,
  onResetInitialAdd?: () => void,
  showMyProductsOnly?: boolean,
  initialFarmerId?: string | null,
  onClearInitialFarmerId?: () => void
}) {
  const canManageProducts = isAdmin || userRole === 'farmer' || userRole === 'aggregator';

  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (initialShowAdd && canManageProducts) {
      setShowAddModal(true);
      if (onResetInitialAdd) onResetInitialAdd();
    }
  }, [initialShowAdd, canManageProducts, onResetInitialAdd]);
  const [isListening, setIsListening] = useState(false);
  const [viewingCertificateUrl, setViewingCertificateUrl] = useState<string | null>(null);
  const [farmerProfile, setFarmerProfile] = useState<UserProfile | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [farmerListings, setFarmerListings] = useState<Listing[]>([]);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [userRating, setUserRating] = useState({ rating: 0, feedback: '' });
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [profileSequence, setProfileSequence] = useState<string[]>(['basic', 'cultivation', 'crops', 'area', 'products', 'ratings']);
  const [isEditingSequence, setIsEditingSequence] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState<UserProfile | null>(null);
  const [loadingFarmer, setLoadingFarmer] = useState(false);
  const [editingListing, setEditingListing] = useState<Listing | null>(null);
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error' | 'info'} | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void} | null>(null);

  const categories = ['All', ...(config.productCategories || ['Vegetables', 'Fruits', 'Grains', 'Millets', 'Inputs'])];

  const [newListing, setNewListing] = useState({
    category: config.productCategories?.[0] || 'Vegetables',
    productName: '',
    price: '',
    unit: 'kg',
    harvestDate: new Date().toISOString().split('T')[0],
    shelfLife: '',
    description: '',
    photoUrl: 'https://picsum.photos/seed/farm/400/300'
  });

  const [newProfile, setNewProfile] = useState<any>({
    displayName: auth.currentUser?.displayName || '',
    farmingMethods: config.farmingMethods?.[0] || 'ZBNF',
    location: '',
    phone: '',
    certifications: (config.availableCertifications && config.availableCertifications.length > 0) ? [config.availableCertifications[0]] : [],
    certificateUrl: '',
    certificatePdfUrl: '',
    isVerified: false
  });

  const availableCertifications = config.availableCertifications;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const farmerId = initialFarmerId || params.get('farmerId');
    if (farmerId) {
      // Clear the parameter from the URL if it was there
      if (params.get('farmerId')) {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
      
      handleViewFarmer({ authorId: farmerId } as Listing);
      setShowProfileModal(true);
      if (onClearInitialFarmerId) onClearInitialFarmerId();
    }
  }, [initialFarmerId]);

  const toggleCertification = (cert: string) => {
    setNewProfile(prev => {
      const certifications = (prev.certifications || []).includes(cert)
        ? (prev.certifications || []).filter((c: string) => c !== cert)
        : [...(prev.certifications || []), cert];
      return { ...prev, certifications };
    });
  };

  const openCertificate = (dataUrl: string) => {
    if (!dataUrl) return;
    setViewingCertificateUrl(dataUrl);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, callback: (base64: string) => void, type: 'image' | 'pdf' = 'image') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Firestore has a 1MB limit for the entire document. 
    // Base64 encoding adds ~33% overhead, so we limit the raw file to ~600KB 
    // to ensure the final document stays under the 1MB limit.
    const MAX_SIZE = 600 * 1024; 
    if (file.size > MAX_SIZE) {
      setToast({ message: `File is too large. Please upload a file smaller than 600KB to ensure it can be saved.`, type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (type === 'pdf') {
        callback(event.target?.result as string);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const max = 800;
        if (width > height) {
          if (width > max) {
            height *= max / width;
            width = max;
          }
        } else {
          if (height > max) {
            width *= max / height;
            height = max;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        callback(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = event.target?.result as string;
    };
    reader.onerror = () => {
      setToast({ message: "Failed to read file. Please try again.", type: 'error' });
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const q = collection(db, 'marketplace_listings');
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Listing));
      
      // Filter by current user if showMyProductsOnly is true
      if (showMyProductsOnly && auth.currentUser) {
        docs = docs.filter(doc => doc.authorId === auth.currentUser?.uid);
      }

      // Sort client-side to be robust against missing timestamps
      docs.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const timeB = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return timeB - timeA;
      });
      setListings(docs);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'marketplace_listings');
    });

    if (auth.currentUser) {
      const profileRef = doc(db, 'users', auth.currentUser.uid);
      const unsubscribeProfile = onSnapshot(profileRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as UserProfile;
          setFarmerProfile(data);
          
          if (!showProfileModal) {
            setNewProfile({
              ...data,
              displayName: data.displayName || auth.currentUser?.displayName || '',
            });
          }
        }
      }, (error) => {
        handleFirestoreError(error, OperationType.GET, `users/${auth.currentUser?.uid}`);
      });
      return () => {
        unsubscribe();
        unsubscribeProfile();
      };
    }

    return () => unsubscribe();
  }, [showMyProductsOnly]);

  const handleAddListing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) return;

    const parsedPrice = parseFloat(newListing.price);
    if (isNaN(parsedPrice)) {
      setToast({ message: "Please enter a valid price", type: 'error' });
      return;
    }

    try {
      const listingData: any = {
        ...newListing,
        price: parsedPrice,
      };

      if (editingListing) {
        // Preserve original ownership and verification status when editing
        listingData.authorId = editingListing.authorId;
        listingData.authorName = editingListing.authorName;
        listingData.farmingMethod = editingListing.farmingMethod || '';
        listingData.isVerified = editingListing.isVerified || false;
      } else {
        // Set ownership for new listings
        listingData.authorId = auth.currentUser.uid;
        listingData.authorName = farmerProfile?.displayName || auth.currentUser.displayName || 'Farmer';
        listingData.farmingMethod = farmerProfile?.farmingMethods || '';
        listingData.isVerified = farmerProfile?.isVerified || false;
        listingData.createdAt = serverTimestamp();
      }

      console.log("Attempting to save listing:", listingData);
      if (editingListing) {
        await setDoc(doc(db, 'marketplace_listings', editingListing.id), listingData, { merge: true });
        setToast({ message: t.updateSuccess || "Listing updated successfully!", type: 'success' });
      } else {
        await addDoc(collection(db, 'marketplace_listings'), listingData);
        setToast({ message: t.postSuccess || "Listing posted successfully!", type: 'success' });
      }

      setShowAddModal(false);
      setEditingListing(null);
      setNewListing({
        category: 'Vegetables',
        productName: '',
        price: '',
        unit: 'kg',
        harvestDate: new Date().toISOString().split('T')[0],
        shelfLife: '',
        description: '',
        photoUrl: 'https://picsum.photos/seed/farm/400/300'
      });
    } catch (error) {
      handleFirestoreError(error, editingListing ? OperationType.UPDATE : OperationType.CREATE, editingListing ? `marketplace_listings/${editingListing.id}` : 'marketplace_listings');
    }
  };

  const handleEditListing = (listing: Listing) => {
    setEditingListing(listing);
    setNewListing({
      category: listing.category,
      productName: listing.productName,
      price: listing.price.toString(),
      unit: listing.unit,
      harvestDate: listing.harvestDate,
      shelfLife: listing.shelfLife,
      description: listing.description,
      photoUrl: listing.photoUrl
    });
    setShowAddModal(true);
  };

  const handleDeleteListing = async (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: t.deleteConfirm || "Are you sure?",
      message: "This listing will be permanently removed from the marketplace.",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'marketplace_listings', id));
          setToast({ message: "Listing deleted successfully", type: 'success' });
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `marketplace_listings/${id}`);
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) return;

    if (!newProfile.displayName?.trim()) {
      setToast({ message: "Name is required", type: 'error' });
      return;
    }

    try {
      const profileData = {
        ...newProfile,
        uid: auth.currentUser.uid,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(doc(db, 'users', auth.currentUser.uid), profileData, { merge: true });
      
      // Sync profile changes to all existing listings of this farmer
      const listingsRef = collection(db, 'marketplace_listings');
      const q = query(listingsRef, where('authorId', '==', auth.currentUser.uid));
      let querySnapshot;
      try {
        querySnapshot = await getDocs(q);
      } catch (error) {
        handleFirestoreError(error, OperationType.LIST, 'marketplace_listings');
        return;
      }
      
      if (querySnapshot && !querySnapshot.empty) {
        const batch = writeBatch(db);
        querySnapshot.forEach((listingDoc) => {
          batch.update(listingDoc.ref, { 
            authorName: profileData.displayName,
            farmingMethod: profileData.farmingMethods,
            isVerified: profileData.isVerified || false
          });
        });
        await batch.commit();
      }
      
      setShowProfileModal(false);
      setToast({ message: t.profileUpdateSuccess || "Profile updated successfully!", type: 'success' });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `users/${auth.currentUser.uid}`);
    }
  };

  const handleViewFarmer = async (listing: Listing) => {
    if (!listing.authorId) {
      setToast({ message: "Farmer information not available", type: 'error' });
      return;
    }
    setSelectedListing(listing);
    setLoadingFarmer(true);
    try {
      // 1. Fetch user profile
      let userData: UserProfile | null = null;
      let actualUid = listing.authorId;

      // Try by UID first
      const userDoc = await getDoc(doc(db, 'users', listing.authorId));
      if (userDoc.exists()) {
        userData = { ...userDoc.data(), uid: listing.authorId } as UserProfile;
      } else {
        // Try by farmerId field
        const q = query(collection(db, 'users'), where('farmerId', '==', listing.authorId));
        const querySnap = await getDocs(q);
        if (!querySnap.empty) {
          const snap = querySnap.docs[0];
          userData = { ...snap.data(), uid: snap.id } as UserProfile;
          actualUid = snap.id;
        }
      }

      if (userData) {
        setSelectedFarmer(userData);
        setProfileSequence(userData.profileSequence || ['basic', 'cultivation', 'crops', 'area', 'products', 'ratings']);
        
        // 2. Fetch farmer's products (marketplace_listings)
        try {
          const q = query(collection(db, 'marketplace_listings'), where('authorId', '==', actualUid));
          const snapshots = await getDocs(q);
          const products = snapshots.docs.map(d => ({ id: d.id, ...d.data() } as Listing));
          setFarmerListings(products);
        } catch (listingError) {
          console.error("Error fetching farmer listings:", listingError);
          setFarmerListings([]);
        }

        // 3. Fetch ratings (subcollection of user)
        try {
          const rq = query(collection(db, 'users', actualUid, 'ratings'));
          const rSnapshots = await getDocs(rq);
          const ratingsData = rSnapshots.docs.map(d => ({ id: d.id, ...d.data() } as Rating));
          ratingsData.sort((a, b) => {
            const timeA = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
            const timeB = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
            return timeB - timeA;
          });
          setRatings(ratingsData);
        } catch (ratingError) {
          console.error("Error fetching farmer ratings:", ratingError);
          setRatings([]);
        }
      } else {
        // Fallback
        setSelectedFarmer({
          uid: listing.authorId,
          email: '',
          displayName: listing.authorName || 'Unknown Farmer',
          role: 'farmer',
          certifications: [],
          farmingMethods: listing.farmingMethod || 'ZBNF'
        } as UserProfile);
        setFarmerListings([]);
        setRatings([]);
      }
    } catch (error) {
      console.error("Error fetching farmer profile:", error);
      setToast({ message: "Error loading farmer profile", type: 'error' });
    } finally {
      setLoadingFarmer(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!auth.currentUser || !selectedFarmer) return;
    if (userRating.rating === 0) {
      setToast({ message: t.roleRequired, type: 'error' });
      return;
    }

    setIsSubmittingRating(true);
    try {
      const ratingData: Omit<Rating, 'id'> = {
        fromUserId: auth.currentUser.uid,
        fromUserName: auth.currentUser.displayName || 'Anonymous',
        rating: userRating.rating,
        feedback: userRating.feedback,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'users', selectedFarmer.uid, 'ratings'), ratingData);
      
      // Update average rating
      const newTotalRatings = (selectedFarmer.totalRatings || 0) + 1;
      const newAverageRating = ((selectedFarmer.averageRating || 0) * (selectedFarmer.totalRatings || 0) + userRating.rating) / newTotalRatings;
      
      await updateDoc(doc(db, 'users', selectedFarmer.uid), {
        averageRating: newAverageRating,
        totalRatings: newTotalRatings
      });

      setToast({ message: t.saveSuccess, type: 'success' });
      setUserRating({ rating: 0, feedback: '' });
      
      // Refresh
      handleViewFarmer({ authorId: selectedFarmer.uid, authorName: selectedFarmer.displayName } as Listing);
    } catch (error) {
      console.error("Error submitting rating:", error);
      setToast({ message: t.somethingWentWrong, type: 'error' });
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('farmer-qr') as HTMLCanvasElement;
    if (canvas) {
      try {
        const pngUrl = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        downloadLink.download = `farmer-${selectedFarmer?.farmerId || 'profile'}-qr.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        setToast({ message: "QR Code downloaded successfully", type: 'success' });
      } catch (err) {
        console.error("Error downloading QR:", err);
        setToast({ message: "Failed to download QR Code", type: 'error' });
      }
    }
  };

  const handleUpdateSequence = async () => {
    if (!isAdmin || !selectedFarmer) return;
    try {
      await updateDoc(doc(db, 'users', selectedFarmer.uid), {
        profileSequence: profileSequence
      });
      setToast({ message: t.saveSuccess, type: 'success' });
      setIsEditingSequence(false);
    } catch (error) {
      setToast({ message: t.somethingWentWrong, type: 'error' });
    }
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newSequence = [...profileSequence];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newSequence.length) {
      [newSequence[index], newSequence[targetIndex]] = [newSequence[targetIndex], newSequence[index]];
      setProfileSequence(newSequence);
    }
  };

  const startVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window)) {
      setToast({ message: "Voice search is not supported in this browser.", type: 'error' });
      return;
    }

    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.lang = language === 'te' ? 'te-IN' : 'en-US';
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
    };
    recognition.start();
  };

  const filteredListings = listings.filter(l => {
    const matchesSearch = (l.productName || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                         (l.authorName || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || l.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      {/* Header */}
      <div className="bg-[#1b7d36] text-white p-6 rounded-b-[32px] shadow-lg sticky top-0 z-10">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl">
              <ShoppingBag size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold">{showMyProductsOnly ? (language === 'te' ? 'నా ఉత్పత్తులు' : 'My Products') : 'Marketplace'}</h1>
              <p className="text-xs text-white/70">{showMyProductsOnly ? (language === 'te' ? 'మీ అమ్మకాలను నిర్వహించండి' : 'Manage your listings') : 'Direct from Nature to You'}</p>
            </div>
          </div>
          {auth.currentUser && (
            <button 
              onClick={() => {
                setSelectedFarmer(null);
                setShowProfileModal(true);
              }}
              className="bg-white/20 p-2 rounded-full hover:bg-white/30 transition-colors"
            >
              <User size={20} />
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="relative group">
          <input 
            type="text" 
            placeholder={language === 'te' ? "ఉత్పత్తుల కోసం వెతకండి..." : "Search products..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white text-stone-800 pl-12 pr-12 py-3 rounded-2xl shadow-inner focus:ring-2 focus:ring-emerald-400 outline-none transition-all"
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
          <button 
            onClick={startVoiceSearch}
            className={`absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-all ${isListening ? 'bg-rose-500 text-white animate-pulse' : 'text-stone-400 hover:text-emerald-600'}`}
          >
            <Mic size={20} />
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="flex gap-3 overflow-x-auto px-6 py-4 no-scrollbar">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-5 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
              activeCategory === cat 
                ? 'bg-[#1b7d36] text-white shadow-md scale-105' 
                : 'bg-white text-stone-600 border border-stone-200 hover:border-emerald-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Listings Grid */}
      <div className="px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnimatePresence mode="popLayout">
          {loading ? (
            <div className="col-span-full flex flex-col items-center justify-center py-20 text-stone-400">
              <Loader2 className="animate-spin mb-2" size={32} />
              <p>Loading fresh produce...</p>
            </div>
          ) : filteredListings.length === 0 ? (
            <div className="col-span-full text-center py-20 bg-white rounded-3xl border border-dashed border-stone-300">
              <ShoppingBag className="mx-auto text-stone-200 mb-4" size={64} />
              <p className="text-stone-500 font-medium">No products found</p>
              <p className="text-stone-400 text-sm">Be the first to list your natural produce!</p>
            </div>
          ) : (
            filteredListings.map(listing => (
              <motion.div
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                key={listing.id}
                className="bg-white rounded-3xl overflow-hidden shadow-sm border border-stone-100 group hover:shadow-xl transition-all duration-300"
              >
                <div className="relative aspect-[4/3]">
                  <img 
                    src={listing.photoUrl} 
                    alt={listing.productName}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold text-emerald-700 uppercase tracking-wider shadow-sm">
                      {listing.category}
                    </span>
                    {listing.isVerified && (
                      <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1">
                        <CheckCircle size={10} /> Verified
                      </span>
                    )}
                  </div>
                  <div className="absolute top-4 right-4 flex gap-2">
                    {(isAdmin || (auth.currentUser && listing.authorId === auth.currentUser.uid && canManageProducts)) && (
                      <>
                        <button 
                          onClick={() => handleEditListing(listing)}
                          className="bg-white/90 backdrop-blur p-2 rounded-full text-blue-500 shadow-sm hover:bg-blue-500 hover:text-white transition-all"
                        >
                          <Tag size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteListing(listing.id)}
                          className="bg-white/90 backdrop-blur p-2 rounded-full text-rose-500 shadow-sm hover:bg-rose-500 hover:text-white transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="p-5 space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold text-stone-800">{listing.productName}</h3>
                      <p className="text-xs text-stone-500 flex items-center gap-1">
                        <User size={12} /> {listing.authorName} {listing.farmingMethod && (
                          <> • <span className="text-emerald-600 font-semibold">{listing.farmingMethod}</span></>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-black text-[#1b7d36]">₹{listing.price}</p>
                      <p className="text-[10px] text-stone-400 uppercase font-bold">per {listing.unit}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-stone-50 p-2 rounded-xl flex items-center gap-2 border border-stone-100">
                      <Calendar className="text-emerald-600" size={14} />
                      <div>
                        <p className="text-[8px] text-stone-400 uppercase font-bold">Harvested</p>
                        <p className="text-[10px] font-bold text-stone-700">{listing.harvestDate}</p>
                      </div>
                    </div>
                    <div className="bg-stone-50 p-2 rounded-xl flex items-center gap-2 border border-stone-100">
                      <Clock className="text-emerald-600" size={14} />
                      <div>
                        <p className="text-[8px] text-stone-400 uppercase font-bold">Shelf Life</p>
                        <p className="text-[10px] font-bold text-stone-700">{listing.shelfLife || 'Fresh'}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-stone-600 line-clamp-2 italic">"{listing.description}"</p>

                  <button 
                    onClick={() => handleViewFarmer(listing)}
                    className="w-full bg-[#1b7d36] text-white py-3 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-[#16652b] transition-colors shadow-md active:scale-95"
                  >
                    Contact Farmer <ChevronRight size={18} />
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Floating Action Button */}
      {auth.currentUser && canManageProducts && (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowAddModal(true)}
          className="fixed bottom-28 right-6 bg-[#1b7d36] text-white p-4 rounded-2xl shadow-2xl z-20"
        >
          <Plus size={28} />
        </motion.button>
      )}

      {/* Add Listing Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 z-50">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              className="bg-white w-full max-w-lg rounded-t-[40px] sm:rounded-[40px] p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-black text-stone-800">{editingListing ? 'Edit Listing' : 'List Your Produce'}</h2>
                <button onClick={() => { setShowAddModal(false); setEditingListing(null); }} className="bg-stone-100 p-2 rounded-full text-stone-400 hover:text-stone-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleAddListing} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-stone-400 uppercase ml-1">Product Image</label>
                  <div className="flex items-center gap-4">
                    <img src={newListing.photoUrl} className="w-24 h-24 rounded-2xl object-cover border border-stone-200" referrerPolicy="no-referrer" />
                    <label className="flex-1 cursor-pointer bg-stone-50 border-2 border-dashed border-stone-200 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-emerald-500 transition-colors">
                      <Upload size={24} className="text-stone-400 mb-1" />
                      <span className="text-xs font-bold text-stone-500">Upload Photo</span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleFileUpload(e, (base64) => setNewListing(prev => ({...prev, photoUrl: base64})))} />
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Category</label>
                    <select 
                      value={newListing.category}
                      onChange={e => setNewListing({...newListing, category: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {categories.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Product Name</label>
                    <input 
                      required
                      type="text" 
                      placeholder="e.g. Organic Tomatoes"
                      value={newListing.productName}
                      onChange={e => setNewListing({...newListing, productName: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Price (₹)</label>
                    <input 
                      required
                      type="number" 
                      placeholder="0.00"
                      value={newListing.price}
                      onChange={e => setNewListing({...newListing, price: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Unit</label>
                    <select 
                      value={newListing.unit}
                      onChange={e => setNewListing({...newListing, unit: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="kg">per kg</option>
                      <option value="quintal">per quintal</option>
                      <option value="dozen">per dozen</option>
                      <option value="liter">per liter</option>
                      <option value="piece">per piece</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Harvest Date</label>
                    <input 
                      type="date" 
                      value={newListing.harvestDate}
                      onChange={e => setNewListing({...newListing, harvestDate: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Shelf Life</label>
                    <input 
                      type="text" 
                      placeholder="e.g. 5-7 days"
                      value={newListing.shelfLife}
                      onChange={e => setNewListing({...newListing, shelfLife: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-stone-400 uppercase ml-1">Description</label>
                  <textarea 
                    placeholder="Tell buyers about your produce..."
                    value={newListing.description}
                    onChange={e => setNewListing({...newListing, description: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500 min-h-[100px]"
                  />
                </div>

                <button 
                  type="submit"
                  className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:bg-[#16652b] transition-all active:scale-95"
                >
                  Post Listing
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Selected Farmer Details Modal */}
      <AnimatePresence>
        {selectedListing && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-[40px] shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-8 pb-4 flex justify-between items-center shrink-0">
                <h2 className="text-2xl font-black text-stone-800">Product Details</h2>
                <button onClick={() => { setSelectedListing(null); setSelectedFarmer(null); }} className="bg-stone-100 p-2 rounded-full text-stone-400 hover:text-stone-600">
                  <X size={24} />
                </button>
              </div>

              <div className="p-8 pt-0 overflow-y-auto flex-1">
                <div className="space-y-6">
                <img 
                  src={selectedListing.photoUrl} 
                  className="w-full aspect-video object-cover rounded-3xl shadow-md" 
                  referrerPolicy="no-referrer"
                />
                
                <div>
                  <div className="flex justify-between items-start">
                    <h3 className="text-2xl font-bold text-stone-800">{selectedListing.productName}</h3>
                    <p className="text-2xl font-black text-[#1b7d36]">₹{selectedListing.price}/{selectedListing.unit}</p>
                  </div>
                  <p className="text-stone-600 mt-2">{selectedListing.description}</p>
                </div>

                <div className="bg-stone-50 p-6 rounded-3xl border border-stone-100 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest">Farmer Information</h4>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setShowQRModal(true)}
                        className="p-2 bg-emerald-100 text-emerald-600 rounded-xl hover:bg-emerald-200 transition-colors"
                        title={t.viewProfileQR}
                      >
                        <QrCode size={18} />
                      </button>
                    </div>
                  </div>
                  {loadingFarmer ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="animate-spin text-emerald-600" size={24} />
                    </div>
                  ) : selectedFarmer ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 overflow-hidden">
                          {selectedFarmer.photoURL ? (
                            <img src={selectedFarmer.photoURL} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User size={24} />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-stone-800">{selectedFarmer.displayName}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-emerald-600 font-bold uppercase">{selectedFarmer.role}</p>
                            {selectedFarmer.averageRating && (
                              <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                                <Star size={10} className="fill-amber-400 text-amber-400" />
                                <span className="text-[10px] font-bold text-amber-700">{selectedFarmer.averageRating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setShowProfileModal(true)}
                          className="p-2 bg-stone-200 text-stone-600 rounded-xl hover:bg-stone-300 transition-colors"
                        >
                          <ChevronRight size={20} />
                        </button>
                      </div>

                      <div className="space-y-2 pt-2 border-t border-stone-200">
                        {selectedFarmer.location && (
                          <div className="flex items-center gap-3 text-stone-600">
                            <MapPin size={18} className="text-stone-400" />
                            <span className="text-sm">{selectedFarmer.location}</span>
                          </div>
                        )}
                        {selectedFarmer.phone && (
                          <div className="flex items-center gap-3 text-stone-600">
                            <Phone size={18} className="text-stone-400" />
                            <a href={`tel:${selectedFarmer.phone}`} className="text-sm font-bold text-[#1b7d36] hover:underline">
                              {selectedFarmer.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-stone-400 text-sm italic">Farmer profile details not available.</p>
                  )}
                </div>

                <a 
                  href={`tel:${selectedFarmer?.phone}`}
                  className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:bg-[#16652b] transition-all flex items-center justify-center gap-3"
                >
                  <Phone size={20} /> Call Farmer
                </a>
              </div>
            </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Farmer Profile Modal */}
      <AnimatePresence>
        {showProfileModal && !selectedFarmer && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-[40px] shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-8 pb-4 flex justify-between items-center shrink-0">
                <h2 className="text-2xl font-black text-stone-800">Farmer Profile</h2>
                <button 
                  onClick={() => {
                    setShowProfileModal(false);
                    setSelectedFarmer(null);
                  }} 
                  className="bg-stone-100 p-2 rounded-full text-stone-400 hover:text-stone-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-8 pt-0 overflow-y-auto flex-1">
                <form onSubmit={handleSaveProfile} className="space-y-5">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-24 h-24 bg-emerald-100 rounded-3xl flex items-center justify-center text-emerald-600 mb-2 border-4 border-white shadow-lg">
                    <User size={48} />
                  </div>
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest">{newProfile.farmingMethods} Farmer</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-stone-400 uppercase ml-1">Full Name</label>
                  <input 
                    required
                    type="text" 
                    value={newProfile.displayName}
                    onChange={e => setNewProfile({...newProfile, displayName: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {config.visibleFields.farmingMethods && (
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Farming Method</label>
                    <select 
                      value={newProfile.farmingMethods}
                      onChange={e => setNewProfile({...newProfile, farmingMethods: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {(config.farmingMethods || ['ZBNF', 'Natural', 'Organic']).map(method => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {config.visibleFields.location && (
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-stone-400 uppercase ml-1">Location</label>
                      <div className="relative">
                        <input 
                          type="text" 
                          placeholder="Village, District"
                          value={newProfile.location}
                          onChange={e => setNewProfile({...newProfile, location: e.target.value})}
                          className="w-full bg-stone-50 border border-stone-200 p-3 pl-10 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      </div>
                    </div>
                  )}
                  {config.visibleFields.contact && (
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-stone-400 uppercase ml-1">Contact</label>
                      <div className="relative">
                        <input 
                          type="tel" 
                          placeholder="Phone Number"
                          value={newProfile.phone}
                          onChange={e => setNewProfile({...newProfile, phone: e.target.value})}
                          className="w-full bg-stone-50 border border-stone-200 p-3 pl-10 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4 pt-4 border-t border-stone-100">
                  <h3 className="text-sm font-bold text-stone-800 uppercase tracking-wider">Cultivation Practices</h3>
                  
                  <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase mb-2">Certification Type (Tap to toggle)</p>
                    <div className="flex flex-wrap gap-2">
                      {availableCertifications.map(c => (
                        <button 
                          key={c}
                          type="button"
                          onClick={() => toggleCertification(c)}
                          className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all ${
                            (newProfile.certifications || []).includes(c)
                              ? 'bg-emerald-600 text-white shadow-md border-emerald-600'
                              : 'bg-white text-emerald-600 border-emerald-100'
                          } border shadow-sm`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  {config.visibleFields.certificate && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-stone-400 uppercase ml-1">Organic Certificate Image</label>
                        <div className="flex items-center gap-4">
                          {newProfile.certificateUrl ? (
                            <div className="relative w-16 h-16 group">
                              <img src={newProfile.certificateUrl} className="w-full h-full rounded-xl object-cover border border-stone-200" referrerPolicy="no-referrer" />
                              <button 
                                type="button"
                                onClick={() => setNewProfile(prev => ({...prev, certificateUrl: ''}))}
                                className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded-xl bg-stone-50 border border-stone-200 flex items-center justify-center text-stone-300">
                              <ImageIcon size={24} />
                            </div>
                          )}
                          <label className="flex-1 cursor-pointer bg-stone-50 border-2 border-dashed border-stone-200 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-emerald-500 transition-colors">
                            <ImageIcon size={24} className="text-stone-400 mb-1" />
                            <span className="text-[10px] font-bold text-stone-500 text-center">{newProfile.certificateUrl ? 'Change Image' : 'Upload Image'}</span>
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleFileUpload(e, (base64) => setNewProfile(prev => ({...prev, certificateUrl: base64})))} />
                          </label>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-bold text-stone-400 uppercase ml-1">Organic Certificate PDF</label>
                        <div className="flex items-center gap-4">
                          {newProfile.certificatePdfUrl ? (
                            <div className="relative w-16 h-16 group">
                              <div 
                                className="w-full h-full rounded-xl bg-rose-50 flex items-center justify-center text-rose-500 border border-rose-100 cursor-pointer hover:bg-rose-100 transition-colors"
                                onClick={() => setViewingCertificateUrl(newProfile.certificatePdfUrl!)}
                              >
                                <ShieldCheck size={24} />
                              </div>
                              <button 
                                type="button"
                                onClick={() => setNewProfile(prev => ({...prev, certificatePdfUrl: ''}))}
                                className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded-xl bg-stone-50 border border-stone-200 flex items-center justify-center text-stone-300">
                              <Upload size={24} />
                            </div>
                          )}
                          <label htmlFor="pdf-upload" className="flex-1 cursor-pointer bg-stone-50 border-2 border-dashed border-stone-200 rounded-2xl p-4 flex flex-col items-center justify-center hover:border-emerald-500 transition-colors">
                            <Upload size={24} className="text-stone-400 mb-1" />
                            <span className="text-[10px] font-bold text-stone-500 text-center">{newProfile.certificatePdfUrl ? 'Change PDF' : 'Upload PDF'}</span>
                            <input id="pdf-upload" type="file" accept="application/pdf" className="hidden" onChange={e => handleFileUpload(e, (base64) => setNewProfile(prev => ({...prev, certificatePdfUrl: base64})), 'pdf')} />
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Practice Description</label>
                    <textarea 
                      placeholder="Describe your natural farming practices..."
                      value={newProfile.cultivationPractices?.description || ''}
                      onChange={e => setNewProfile({
                        ...newProfile, 
                        cultivationPractices: {
                          ...(newProfile.cultivationPractices || {}),
                          description: e.target.value
                        }
                      })}
                      rows={3}
                      className="w-full bg-stone-50 border border-stone-200 p-3 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase ml-1">Practice Images</label>
                    <div className="grid grid-cols-3 gap-3">
                      {(newProfile.cultivationPractices?.photos || []).map((url: string, index: number) => (
                        <div key={index} className="relative group aspect-square rounded-2xl overflow-hidden border border-stone-200 shadow-sm">
                          <img src={url} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                          <button 
                            type="button"
                            onClick={() => {
                              const newPhotos = [...(newProfile.cultivationPractices.photos)];
                              newPhotos.splice(index, 1);
                              setNewProfile({
                                ...newProfile,
                                cultivationPractices: {
                                  ...newProfile.cultivationPractices,
                                  photos: newPhotos
                                }
                              });
                            }}
                            className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {(newProfile.cultivationPractices?.photos || []).length < 6 && (
                        <label className="aspect-square cursor-pointer bg-stone-50 border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center hover:border-emerald-500 transition-colors">
                          <Plus size={20} className="text-stone-400" />
                          <span className="text-[10px] font-bold text-stone-500 mt-1">Add</span>
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={e => handleFileUpload(e, (base64) => {
                              setNewProfile(prev => ({
                                ...prev,
                                cultivationPractices: {
                                  ...(prev.cultivationPractices || {}),
                                  photos: [...(prev.cultivationPractices?.photos || []), base64]
                                }
                              }));
                            })} 
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:bg-[#16652b] transition-all active:scale-95"
                >
                  Save Profile
                </button>
              </form>
            </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Farmer Profile Modal (Viewing) */}
      <AnimatePresence>
        {showProfileModal && selectedFarmer && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full max-w-lg rounded-[40px] shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-8 pb-4 flex justify-between items-center shrink-0 bg-emerald-600 text-white">
                <h2 className="text-2xl font-black">{t.farmerProfile}</h2>
                <div className="flex gap-2">
                  {isAdmin && (
                    <button
                      onClick={() => setIsEditingSequence(!isEditingSequence)}
                      className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                      title={t.manageProfileSequence}
                    >
                      <Move size={20} />
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      setShowProfileModal(false);
                      setSelectedFarmer(null);
                    }} 
                    className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-8 overflow-y-auto flex-1 space-y-8 bg-stone-50">
                {isEditingSequence && (
                  <div className="bg-white p-6 rounded-3xl shadow-sm border border-emerald-100 space-y-4">
                    <h3 className="font-bold text-stone-800 flex items-center gap-2">
                      <Move size={18} className="text-emerald-600" />
                      {t.arrangeProfile}
                    </h3>
                    <div className="space-y-2">
                      {profileSequence.map((item, index) => (
                        <div key={item} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl border border-stone-100">
                          <span className="text-sm font-bold text-stone-600 capitalize">{item}</span>
                          <div className="flex gap-1">
                            <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="p-1 text-stone-400 hover:text-emerald-600 disabled:opacity-30">
                              <ChevronUp size={18} />
                            </button>
                            <button onClick={() => moveItem(index, 'down')} disabled={index === profileSequence.length - 1} className="p-1 text-stone-400 hover:text-emerald-600 disabled:opacity-30">
                              <ChevronDown size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={handleUpdateSequence}
                      className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all"
                    >
                      {t.saveSequence}
                    </button>
                  </div>
                )}

                {profileSequence.map((item) => {
                  switch (item) {
                    case 'basic':
                      return (
                        <div key="basic" className="flex flex-col items-center">
                          <div className="w-24 h-24 bg-emerald-100 rounded-3xl flex items-center justify-center text-emerald-600 mb-4 border-4 border-white shadow-xl overflow-hidden">
                            {selectedFarmer.photoURL ? (
                              <img src={selectedFarmer.photoURL} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <User size={48} />
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-stone-800">{selectedFarmer.displayName}</h3>
                          <div className="flex flex-col items-center gap-1 mb-2">
                            <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest">{selectedFarmer.role}</p>
                            {selectedFarmer.farmingMethods && (
                              <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider bg-stone-50 px-2 py-0.5 rounded border border-stone-100">
                                {selectedFarmer.farmingMethods} {language === 'te' ? 'రైతు' : 'Farmer'}
                              </p>
                            )}
                          </div>
                          {selectedFarmer.farmerId && (
                            <p className="text-xs text-stone-400 font-mono bg-stone-100 px-3 py-1 rounded-full">{selectedFarmer.farmerId}</p>
                          )}
                          {(selectedFarmer.certificateUrl || selectedFarmer.certificatePdfUrl || (selectedFarmer.certifications && selectedFarmer.certifications.length > 0)) && (
                            <div className="flex items-center gap-1.5 mt-3 bg-emerald-50 px-4 py-1.5 rounded-full border border-emerald-100">
                              <ShieldCheck size={14} className="text-emerald-600" />
                              <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Certified Natural Farmer</span>
                            </div>
                          )}
                        </div>
                      );
                    case 'cultivation':
                      return (selectedFarmer.cultivationPractices || selectedFarmer.certifications?.length > 0 || selectedFarmer.certificateUrl || selectedFarmer.certificatePdfUrl) && (
                        <div key="cultivation" className="bg-white p-6 rounded-3xl shadow-sm border border-stone-100 space-y-4">
                          <h4 className="font-bold text-emerald-800 flex items-center gap-2 border-b border-emerald-50 pb-2">
                            <Leaf size={18} />
                            {t.cultivationPractices}
                          </h4>
                          
                          {selectedFarmer.certifications && selectedFarmer.certifications.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {selectedFarmer.certifications.map(c => (
                                <span key={c} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-bold border border-emerald-100 shadow-sm">
                                  {c}
                                </span>
                              ))}
                            </div>
                          )}

                          {selectedFarmer.cultivationPractices?.description && (
                            <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-wrap">
                              {selectedFarmer.cultivationPractices.description}
                            </p>
                          )}

                          {(selectedFarmer.certificateUrl || selectedFarmer.certificatePdfUrl) && (
                            <div className="space-y-4 py-2">
                              {selectedFarmer.certificateUrl && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Organic Certificate Image</p>
                                    <button 
                                      onClick={() => setViewingCertificateUrl(selectedFarmer.certificateUrl!)}
                                      className="text-[10px] font-bold text-emerald-600 hover:underline"
                                    >
                                      View Full Size
                                    </button>
                                  </div>
                                  <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm aspect-video bg-stone-50 group relative">
                                    <img 
                                      src={selectedFarmer.certificateUrl} 
                                      className="w-full h-full object-contain cursor-pointer transition-transform group-hover:scale-105" 
                                      onClick={() => setViewingCertificateUrl(selectedFarmer.certificateUrl!)}
                                      referrerPolicy="no-referrer"
                                      alt="Organic Certificate"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none flex items-center justify-center">
                                      <Search size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {selectedFarmer.certificatePdfUrl && (
                                <div className="space-y-2">
                                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Organic Certificate PDF</p>
                                  <div className="flex flex-col gap-2">
                                    <a 
                                      href={selectedFarmer.certificatePdfUrl} 
                                      download="organic-certificate.pdf"
                                      className="flex items-center justify-center gap-2 w-full py-3 bg-stone-100 text-stone-600 rounded-2xl text-xs font-bold border border-stone-200 hover:bg-stone-200 transition-colors"
                                    >
                                      <Download size={16} />
                                      Download PDF
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {selectedFarmer.cultivationPractices?.photos && selectedFarmer.cultivationPractices.photos.length > 0 && (
                            <div className="grid grid-cols-2 gap-2 mt-4">
                              {selectedFarmer.cultivationPractices.photos.map((url, i) => (
                                <img key={i} src={url} alt="" className="w-full h-32 object-cover rounded-2xl border border-stone-100" />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    case 'crops':
                      return selectedFarmer.crops && selectedFarmer.crops.length > 0 && (
                        <div key="crops" className="bg-white p-6 rounded-3xl shadow-sm border border-stone-100 space-y-4">
                          <h4 className="font-bold text-emerald-800 flex items-center gap-2 border-b border-emerald-50 pb-2">
                            <Calendar size={18} />
                            {t.cropDetails}
                          </h4>
                          <div className="grid grid-cols-1 gap-3">
                            {selectedFarmer.crops.map((crop) => (
                              <div key={crop.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl border border-stone-100">
                                <div>
                                  <p className="font-bold text-stone-800 text-sm">{crop.name}</p>
                                  {crop.area && <p className="text-[10px] text-stone-400 uppercase tracking-wider">{language === 'te' ? 'ఎకరాలు' : 'Acres'} {crop.area}</p>}
                                </div>
                                <div className="text-right">
                                  <p className="text-[10px] text-emerald-600 font-bold uppercase">{t.estimatedHarvest}</p>
                                  <p className="text-xs font-bold text-stone-600">{crop.harvestTime}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    case 'area':
                      return selectedFarmer.cultivationArea && (
                        <div key="area" className="bg-white p-6 rounded-3xl shadow-sm border border-stone-100 space-y-4">
                          <h4 className="font-bold text-emerald-800 flex items-center gap-2 border-b border-emerald-50 pb-2">
                            <MapPin size={18} />
                            {t.cultivationArea}
                          </h4>
                          <div className="space-y-4">
                            <p className="text-sm font-medium text-stone-700">{selectedFarmer.cultivationArea.description}</p>
                            <div className="flex gap-4">
                              <div className="flex-1 bg-stone-50 p-2 rounded-xl text-center">
                                <p className="text-[8px] text-stone-400 uppercase font-bold">{t.latitude}</p>
                                <p className="text-xs font-mono font-bold text-stone-600">{selectedFarmer.cultivationArea.lat?.toFixed(6)}</p>
                              </div>
                              <div className="flex-1 bg-stone-50 p-2 rounded-xl text-center">
                                <p className="text-[8px] text-stone-400 uppercase font-bold">{t.longitude}</p>
                                <p className="text-xs font-mono font-bold text-stone-600">{selectedFarmer.cultivationArea.lng?.toFixed(6)}</p>
                              </div>
                            </div>
                            
                            <CultivationMap 
                              lat={selectedFarmer.cultivationArea.lat}
                              lng={selectedFarmer.cultivationArea.lng}
                              readOnly={true}
                            />
                          </div>
                        </div>
                      );
                    {/* Products section removed as per user request */}
                    case 'ratings':
                      return (
                        <div key="ratings" className="bg-white p-6 rounded-3xl shadow-sm border border-stone-100 space-y-6">
                          <div className="flex items-center justify-between border-b border-emerald-50 pb-2">
                            <h4 className="font-bold text-emerald-800 flex items-center gap-2">
                              <Star size={18} />
                              {t.ratingsAndFeedback}
                            </h4>
                            {selectedFarmer.averageRating && (
                              <div className="flex items-center gap-1 bg-amber-50 px-3 py-1 rounded-full">
                                <Star size={14} className="fill-amber-400 text-amber-400" />
                                <span className="text-sm font-bold text-amber-700">{selectedFarmer.averageRating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>

                          {auth.currentUser && auth.currentUser.uid !== selectedFarmer.uid && (
                            <div className="space-y-4 p-4 bg-stone-50 rounded-2xl border border-stone-100">
                              <p className="text-sm font-bold text-stone-700">{t.provideFeedback}</p>
                              <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <button
                                    key={s}
                                    onClick={() => setUserRating({ ...userRating, rating: s })}
                                    className="p-1"
                                  >
                                    <Star
                                      size={24}
                                      className={`${userRating.rating >= s ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}`}
                                    />
                                  </button>
                                ))}
                              </div>
                              <textarea
                                value={userRating.feedback}
                                onChange={(e) => setUserRating({ ...userRating, feedback: e.target.value })}
                                className="w-full bg-white border border-stone-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-emerald-400 outline-none min-h-[80px]"
                                placeholder={t.feedback}
                              />
                              <button
                                onClick={handleSubmitRating}
                                disabled={isSubmittingRating || userRating.rating === 0}
                                className="w-full bg-emerald-600 text-white py-2 rounded-xl font-bold text-sm shadow-md hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {isSubmittingRating ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />}
                                {t.submitFeedback}
                              </button>
                            </div>
                          )}

                          <div className="space-y-4">
                            {ratings.map((r) => (
                              <div key={r.id} className="border-b border-stone-50 pb-4 last:border-0">
                                <div className="flex justify-between mb-1">
                                  <p className="text-xs font-bold text-stone-800">{r.fromUserName}</p>
                                  <div className="flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                      <Star key={s} size={10} className={`${r.rating >= s ? 'fill-amber-400 text-amber-400' : 'text-stone-200'}`} />
                                    ))}
                                  </div>
                                </div>
                                <p className="text-xs text-stone-600 italic">"{r.feedback}"</p>
                              </div>
                            ))}
                            {ratings.length === 0 && <p className="text-xs text-stone-400 italic text-center">No feedback yet.</p>}
                          </div>
                        </div>
                      );
                    default:
                      return null;
                  }
                })}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* QR Code Modal */}
      <AnimatePresence>
        {showQRModal && selectedFarmer && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white w-full max-w-xs rounded-[40px] shadow-2xl overflow-hidden p-8 flex flex-col items-center text-center gap-6"
            >
              <div className="flex justify-between items-center w-full">
                <h3 className="font-bold text-stone-800">{t.viewProfileQR}</h3>
                <button onClick={() => setShowQRModal(false)} className="p-1 text-stone-400">
                  <X size={20} />
                </button>
              </div>

              <div className="bg-white p-4 rounded-3xl shadow-inner border border-stone-100">
                <QRCodeCanvas
                  id="farmer-qr"
                  value={`${window.location.origin}${window.location.pathname}?farmerId=${selectedFarmer.uid}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>

              <div>
                <p className="font-bold text-stone-800">{selectedFarmer.displayName}</p>
                <p className="text-xs text-stone-400">{selectedFarmer.farmerId || selectedFarmer.email}</p>
              </div>

              <button
                onClick={downloadQRCode}
                className="w-full bg-stone-800 text-white py-3 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-stone-900 transition-all"
              >
                <Download size={20} />
                {t.downloadQR}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notifications */}
      <AnimatePresence>
        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        )}
      </AnimatePresence>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
          type="danger"
        />
      )}
      {/* Certificate Viewing Modal */}
      {viewingCertificateUrl && (
        <div className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4" onClick={() => setViewingCertificateUrl(null)}>
          <div className="relative max-w-4xl w-full h-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end p-2">
              <button 
                onClick={() => setViewingCertificateUrl(null)}
                className="text-white p-2 hover:bg-white/10 rounded-full"
              >
                <X size={32} />
              </button>
            </div>
            
            <div className="flex-1 bg-stone-900 rounded-xl overflow-hidden shadow-2xl flex flex-col">
              {viewingCertificateUrl.startsWith('data:application/pdf') || viewingCertificateUrl.toLowerCase().includes('.pdf') || viewingCertificateUrl.toLowerCase().includes('certificate') ? (
                <div className="w-full h-full flex flex-col bg-white">
                  <div className="flex justify-between items-center px-4 py-3 bg-stone-100 border-b border-stone-200 shrink-0">
                    <div className="flex items-center gap-2">
                      <FileText size={18} className="text-rose-600" />
                      <span className="text-sm font-bold text-stone-700">PDF Certificate Viewer</span>
                    </div>
                    <a 
                      href={viewingCertificateUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700 underline flex items-center gap-1"
                    >
                      Open in New Tab
                    </a>
                  </div>
                  <div className="flex-1 relative bg-stone-200">
                    <object 
                      data={viewingCertificateUrl} 
                      type="application/pdf" 
                      className="w-full h-full"
                    >
                      <div className="flex flex-col items-center justify-center p-12 text-center h-full bg-stone-800 text-white">
                        <FileText size={64} className="mb-6 text-stone-500" />
                        <h3 className="text-lg font-bold mb-2">PDF Viewer Not Available</h3>
                        <p className="text-stone-400 mb-8 max-w-xs mx-auto">Your browser cannot display this PDF inline. Please use the button below to view it.</p>
                        <a 
                          href={viewingCertificateUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold shadow-lg transition-all flex items-center gap-2"
                        >
                          Open PDF in New Window
                        </a>
                      </div>
                    </object>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <img 
                    src={viewingCertificateUrl} 
                    className="max-w-full max-h-full object-contain rounded-lg" 
                    referrerPolicy="no-referrer"
                    alt="Certificate"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
